import { useDateContext } from '_/hooks/DateContext';
import { useEventContext } from '_/hooks/EventContext';
import { RecurrenceType } from '_/services/models/enums/recurrence-type.enum';
import { endOfYear } from 'date-fns';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RRule } from 'rrule';
import CustomDropDownFieldValuesModal from '../CustomDropDownFieldModal';
import { SpaceType } from '_/services/models/enums/space-type.enum';

interface SelectRecurrenceModalProps {
  visible: boolean;
  setRecurrenceModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setRecurrenceType: (type: RecurrenceType) => void;
  recurrenceType: RecurrenceType;
  spaceType: SpaceType;
}

export default function SelectRecurrenceModal({
  visible,
  setRecurrenceType,
  recurrenceType,
  setRecurrenceModalVisible,
  setCustomModalVisible,
  spaceType,
}: SelectRecurrenceModalProps) {
  const [customRecurrence, setCustomRecurrence] = useState(false);
  const { selectedEventDate } = useDateContext();
  const { setRecurrenceStr } = useEventContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (!visible && customRecurrence) {
      const timer = setTimeout(() => {
        setCustomModalVisible(true);
        setCustomRecurrence(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [visible, customRecurrence]);

  const handleRecurrenceType = (value: string) => {
    if (value === t('recurrenceTypes.ONCE')) {
      handleJustOnce();
    } else if (value === t('recurrenceTypes.DAILY')) {
      handleDaily();
    } else if (value === t('recurrenceTypes.CUSTOM')) {
      handleCustom();
    }
  };

  const handleJustOnce = () => {
    setRecurrenceType(RecurrenceType.ONCE);
    setRecurrenceModalVisible(false);
    setRecurrenceStr('');
  };

  const handleDaily = () => {
    setRecurrenceType(RecurrenceType.DAILY);
    setRecurrenceModalVisible(false);
    const rule = new RRule({
      freq: RRule.DAILY,
      dtstart: selectedEventDate.start,
      until: endOfYear(new Date()),
    });
    setRecurrenceStr(rule.toString());
  };

  const handleCustom = () => {
    setRecurrenceType(RecurrenceType.CUSTOM);
    setCustomRecurrence(true);
    setRecurrenceModalVisible(false);
  };

  return (
    <CustomDropDownFieldValuesModal
      hideDropdown={() => setRecurrenceModalVisible(false)}
      label={t('workstationScreen.recurrenceModal.recurrence')}
      setDropdownField={handleRecurrenceType}
      value={t('recurrenceTypes.' + recurrenceType)}
      options={Object.values(RecurrenceType).map((type) => t('recurrenceTypes.' + type))}
      visible={visible}
      spaceType={spaceType}
    />
  );
}
